// CandidatesLinkButton.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import '../pages/Representatives.css';

/**
 * CandidatesLinkButton
 *
 * Sends the user from their representatives to the candidate list for the same
 * place.
 *
 * Renders as a bare pill with no wrapper: it sits inside the existing tabs
 * flex row, and a wrapper with width:100% claimed the whole remaining row and
 * left a gap the size of the row beside it.
 *
 * Markup mirrors the pills it sits among - a plain button carrying
 * .reps-precision-nudge - so it themes with them rather than reading as a
 * foreign control.
 *
 * Deliberately a link to a filtered list rather than a per-official link to one
 * candidate. Officials carry a bioGuide ID and federal candidates carry an FEC
 * ID, with no stored mapping between them - reconstructing one from surname and
 * seat can be wrong, and a link that sends someone to another person's campaign
 * finances is worse on a transparency site than making them pick from a list.
 * Below federal level the reasoning is different but the answer is the same:
 * those filings carry no ID at all, so there is no per-candidate address to
 * link to.
 *
 * The county travels in navigation state rather than in the path. The
 * destination route is /us/candidates/:stateAbbr and county is a toggle there,
 * not a segment; passing it this way means somebody who has already told the
 * app where they live lands on their own county instead of a state-wide list
 * they have to narrow by hand. A pasted link has no navigation state and falls
 * back to the whole state, which is the correct behaviour for a link that
 * carries no county in it.
 *
 * Renders nothing without a state, since the destination would have no filter
 * to apply.
 *
 * @param {Object} props
 * @param {string} props.stateAbbr - Two letter code from the saved location.
 * @param {string} [props.county]  - County name, if one has been resolved.
 */
function CandidatesLinkButton({ stateAbbr, county }) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  if (!stateAbbr) return null;

  return (
    <button
      type="button"
      className="reps-precision-nudge"
      onClick={() =>
        navigate(`/us/candidates/${String(stateAbbr).toUpperCase()}`, {
          state: county ? { county, level: 'county' } : undefined,
        })
      }
    >
      {/* New key rather than common:viewCampaignFinance. That one is already in
          the locale files translated as campaign finance, and i18next only
          falls back when a key is missing - so editing the fallback string here
          would change nothing for any translated user. The wording had to move
          anyway: campaign finance is a federal idea, and a county filing
          carries a phone number, not a finance report. */}
      {t('common:candidatesTab', 'Candidates')}
    </button>
  );
}

export default CandidatesLinkButton;